import type { ReactNode } from 'react';
import { Button, Space, Tooltip } from 'antd';
import { EditOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { DescriptionsItemType } from 'antd/es/descriptions';
import type { TFunction } from 'i18next';
import { Guard } from '@ohpen/react-authz';

import { lockActionTitle } from '@pmp/utils';

import styles from './styles.module.css';

import {
  CompliancePermissionLevel,
  CompliancePermissionLevelPerStatus,
  ComplianceStatus,
  LenderDraftDto,
  LenderResponse,
  ComplianceSettings,
} from '@pmp/api';

import type { CompliancePermissionMap, ComplianceFormValues } from './interfaces';

const labels = 'lender.lender.labels';

type ProblemDetails = Record<string, unknown>;

const parseProblem = (error: unknown, depth = 0): ProblemDetails | undefined => {
  if (depth > 4 || error == null) return undefined;
  if (typeof error === 'string') {
    const s = error.trim();
    if (!s.startsWith('{')) return undefined;
    try {
      return JSON.parse(s) as ProblemDetails;
    } catch {
      return undefined;
    }
  }
  if (typeof error !== 'object') return undefined;
  const rec = error as ProblemDetails;
  // Only a payload key identifies the body itself: the auth client wraps it in an ApiError that
  // carries `status` too, and matching on that would stop here and leave the field errors unread.
  if ('invalid-params' in rec || 'errors' in rec || 'detail' in rec) return rec;
  // rec.message covers an Error wrapping the raw response text.
  for (const nested of [rec.error, rec.data, rec.response, rec.body, rec.message]) {
    const found = parseProblem(nested, depth + 1);
    if (found) return found;
  }
  return undefined;
};

// Direct-CRUD endpoints throw an HTTP validation body (not the CR flow's {isValid,errors});
// map its fields to enso inline errors. Server fields are PascalCase, form fields camelCase.
export const toCrudFieldErrors = (error: unknown): Record<string, string[]> | undefined => {
  const pd = parseProblem(error);
  if (!pd) return undefined;
  const out: Record<string, string[]> = {};
  const toField = (k: string) => k.charAt(0).toLowerCase() + k.slice(1);
  const push = (field: string, msgs: string[]) => {
    if (msgs.length) out[field] = [...(out[field] ?? []), ...msgs];
  };

  const params = pd['invalid-params'];
  if (Array.isArray(params)) {
    for (const p of params as Array<{ name?: string; reason?: string }>) {
      if (p?.name && p?.reason) push(toField(p.name), [p.reason]);
    }
  }
  const errors = pd.errors;
  if (errors && typeof errors === 'object') {
    for (const [k, v] of Object.entries(errors as Record<string, unknown>)) {
      push(
        toField(k),
        (Array.isArray(v) ? v : [v]).filter((m): m is string => typeof m === 'string'),
      );
    }
  }
  return Object.keys(out).length ? out : undefined;
};

export const SETTABLE_COMPLIANCE_STATUSES: ComplianceStatus[] = [
  ComplianceStatus.PENDING,
  ComplianceStatus.FAILED,
  ComplianceStatus.PASSED,
];

export const COMPLIANCE_PERMISSION_LEVELS: CompliancePermissionLevel[] = [
  CompliancePermissionLevel.BLOCK,
  CompliancePermissionLevel.VIEW,
  CompliancePermissionLevel.ILLUSTRATION,
  CompliancePermissionLevel.DIP,
  CompliancePermissionLevel.FMA,
  CompliancePermissionLevel.FULL_ACCESS,
];

// Levels that grant more than a read-only view; on Pending they let never-assessed brokers in.
const PERMISSIVE_LEVELS = new Set<CompliancePermissionLevel>([
  CompliancePermissionLevel.ILLUSTRATION,
  CompliancePermissionLevel.DIP,
  CompliancePermissionLevel.FMA,
  CompliancePermissionLevel.FULL_ACCESS,
]);

export const isPermissiveComplianceLevel = (level?: CompliancePermissionLevel): boolean => !!level && PERMISSIVE_LEVELS.has(level);

const toCompliancePermissionMap = (
  data?: Array<{ complianceStatus?: string; permissionLevel?: CompliancePermissionLevel }> | null,
): CompliancePermissionMap => {
  const map: CompliancePermissionMap = {};
  for (const entry of data ?? []) {
    if (entry.complianceStatus && entry.permissionLevel) {
      map[entry.complianceStatus as ComplianceStatus] = entry.permissionLevel;
    }
  }
  return map;
};

export const toCompliancePermissionArray = (map: CompliancePermissionMap): CompliancePermissionLevelPerStatus[] =>
  SETTABLE_COMPLIANCE_STATUSES.filter(status => map[status]).map(status => ({
    complianceStatus: status,
    permissionLevel: map[status],
  }));

// Legacy/retired values (e.g. NOT_REQUIRED before the data migration heals it to PASSED, or
// RE_REVIEW_REQUIRED) are not selectable defaults; drop them so the field shows the placeholder
// and forces a valid pick instead of pre-selecting an unsaveable value.
const sanitizeDefaultStatus = (status?: ComplianceStatus | null): ComplianceStatus | undefined =>
  status && SETTABLE_COMPLIANCE_STATUSES.includes(status) ? status : undefined;

export const toComplianceFormValues = (config?: ComplianceSettings): ComplianceFormValues => ({
  defaultFirmStatus: sanitizeDefaultStatus(config?.defaultFirmStatus),
  defaultIntermediaryStatus: sanitizeDefaultStatus(config?.defaultIntermediaryStatus),
  firmToReviewPermissionLevel: config?.firmToReviewPermissionLevel ?? undefined,
  intermediaryToReviewPermissionLevel: config?.intermediaryToReviewPermissionLevel ?? undefined,
  firmPermissions: toCompliancePermissionMap(config?.firmPermissionLevelPerStatus),
  intermediaryPermissions: toCompliancePermissionMap(config?.intermediaryPermissionLevelPerStatus),
  reviewOnBmpChangesEnabled: config?.reviewOnBmpChangesEnabled ?? false,
  periodicCheckEnabled: config?.periodicCheckEnabled ?? false,
  resetReviewRequiredFlagAfterMonths: config?.resetReviewRequiredFlagAfterMonths ?? null,
});

// The lender draft is one flat DTO covering the whole entity; the form only edits the compliance
// fields, so id/lock/address are replayed from the last fetched lender.
export const toLenderDraftDto = (details: LenderResponse, values: ComplianceFormValues): LenderDraftDto => ({
  id: details.id,
  lockedByActionId: details.lockedByActionId,
  address: details.address,
  defaultFirmStatus: values.defaultFirmStatus,
  defaultIntermediaryStatus: values.defaultIntermediaryStatus,
  firmToReviewPermissionLevel: values.firmToReviewPermissionLevel,
  intermediaryToReviewPermissionLevel: values.intermediaryToReviewPermissionLevel,
  firmPermissionLevelPerStatus: toCompliancePermissionArray(values.firmPermissions),
  intermediaryPermissionLevelPerStatus: toCompliancePermissionArray(values.intermediaryPermissions),
  resetReviewRequiredFlagAfterMonths: values.periodicCheckEnabled ? (values.resetReviewRequiredFlagAfterMonths ?? null) : null,
  reviewOnBmpChangesEnabled: values.reviewOnBmpChangesEnabled,
  periodicCheckEnabled: values.periodicCheckEnabled,
});

const labelWithTooltip = (label: string, tooltip: string): ReactNode => (
  <Space size={4}>
    {label}
    <Tooltip title={tooltip}>
      <QuestionCircleOutlined className={styles.tooltipIcon} />
    </Tooltip>
  </Space>
);

export const getComplianceSettingsItems = (translate: TFunction, config?: ComplianceSettings): DescriptionsItemType[] => {
  const toYesNo = (value?: boolean) => (value ? translate('enso.labels.yes') : translate('enso.labels.no'));

  return [
    {
      key: 'reviewOnBmpChanges',
      label: labelWithTooltip(translate(`${labels}.reviewOnBmpChanges`), translate(`${labels}.reviewOnBmpChangesTooltip`)),
      children: toYesNo(config?.reviewOnBmpChangesEnabled),
      span: 2,
    },
    {
      key: 'periodicCheck',
      label: labelWithTooltip(translate(`${labels}.periodicCheck`), translate(`${labels}.periodicCheckTooltip`)),
      children: toYesNo(config?.periodicCheckEnabled),
    },
    {
      key: 'reviewInterval',
      label: translate(`${labels}.reviewInterval`),
      children: config?.resetReviewRequiredFlagAfterMonths ?? '-',
    },
  ];
};

export const renderComplianceEditAction = (
  translate: TFunction,
  onEditCompliance: () => void,
  lockedByActionId?: string | null,
): ReactNode => (
  <Guard permissions={['lenders:update']} flags={['enableLenderCrud']}>
    <Tooltip title={lockActionTitle('edit', lockedByActionId)} placement="top">
      <Button type="primary" icon={<EditOutlined />} onClick={onEditCompliance} disabled={!!lockedByActionId}>
        {translate('enso.buttons.edit')}
      </Button>
    </Tooltip>
  </Guard>
);

export const getComplianceItems = (
  translate: TFunction,
  data?: Array<CompliancePermissionLevelPerStatus> | null,
  toReviewPermissionLevel?: CompliancePermissionLevel | null,
): DescriptionsItemType[] => {
  // The array's order isn't guaranteed by the backend (firm and intermediary have been seen to
  // differ), so render in a fixed status order rather than whatever order the array happens to be in.
  const permissionByStatus = new Map((data ?? []).map(({ complianceStatus, permissionLevel }) => [complianceStatus, permissionLevel]));
  const items: DescriptionsItemType[] = SETTABLE_COMPLIANCE_STATUSES.filter(status => permissionByStatus.has(status)).map(status => {
    const permissionLevel = permissionByStatus.get(status);
    return {
      key: status,
      label: translate(`${labels}.${status}`, { defaultValue: status }),
      children: permissionLevel ? translate(`${labels}.${permissionLevel}`, { defaultValue: permissionLevel }) : '',
    };
  });

  if (toReviewPermissionLevel) {
    items.push({
      key: 'toReviewPermissionLevel',
      label: translate(`${labels}.reviewRequiredPermission`),
      children: translate(`${labels}.${toReviewPermissionLevel}`, { defaultValue: toReviewPermissionLevel }),
    });
  }

  return items;
};
